import './Header.scss';
import { delivery_icon, bag_icon, dark_logo, white_logo, cart_icon, cart_icon_alt, user_icon, user_icon_alt, arrow_down } from '../../assets/icons';
import locationIcon from '../RestaurantDetailPage/img/location.png';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import UserMenu from '../UserMenu/UserMenu';
import { useAuth } from '../../auth/AuthContext';
import { useDeliveryAddress } from '../../address/DeliveryAddressContext';
import GuestAuthDropdown from '../GuestAuthDropdown/GuestAuthDropdown';
import { useGuestCart } from '../../guestCart/hooks/useGuestCart';
import OrderDetailsCard from './OrderDetailsCard';

function truncate(str, max = 32) {
  const s = String(str || '').trim();
  return s.length > max ? s.slice(0, max) + '…' : s;
}

export default function Header({ white = false }) {
  const [orderCardOpen, setOrderCardOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, customer, logout } = useAuth();
  const { currentAddress, guestGeoBootstrapDone, orderMode, setOrderMode } = useDeliveryAddress();
  const { itemCount, hasItems, actions } = useGuestCart();
  const cls = `header ${white ? 'header--white' : ''}`;
  const headerRef = useRef(null);
  const cartSrc = white ? cart_icon_alt : cart_icon;
  const userSrc = white ? user_icon_alt : user_icon;
  const logoSrc = white ? dark_logo : white_logo;
  const displayName = isAuthenticated
    ? (customer?.name?.trim() || 'Profile')
    : 'Guest';

  const toggleOrderCard = useCallback(() => setOrderCardOpen((v) => !v), []);
  const closeOrderCard = useCallback(() => setOrderCardOpen(false), []);
  const openOrderCardForMode = useCallback((mode) => {
    setOrderMode(mode);
    setOrderCardOpen(true);
  }, [setOrderMode]);

  const addressDisplay = useMemo(() => {
    const raw = String(currentAddress?.address || '').trim();
    if (!raw) return null;
    const line = truncate(raw, 36);
    const source = currentAddress.source || 'manual';
    const tag = source === 'current' ? 'Now' : source === 'default' ? 'Default' : null;
    return { line, tag };
  }, [currentAddress]);

  const addressFallbackLabel = useMemo(() => {
    if (addressDisplay) return null;
    if (!isAuthenticated && !guestGeoBootstrapDone) return 'Finding your location…';
    try {
      if (typeof window !== 'undefined' && window.sessionStorage?.getItem('dindin.geoAutoFillFailed') === '1') {
        return 'Enter your location';
      }
    } catch { /* ignore */ }
    return 'Select delivery address';
  }, [addressDisplay, guestGeoBootstrapDone, isAuthenticated]);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const applyHeight = () => {
      const h = Math.ceil(el.getBoundingClientRect().height || 0);
      if (h > 0) document.documentElement.style.setProperty('--app-header-h', `${h}px`);
    };

    applyHeight();

    const ro = new ResizeObserver(() => applyHeight());
    ro.observe(el);
    window.addEventListener('resize', applyHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', applyHeight);
    };
  }, [white]);

  const openCart = (e) => {
    if (e?.preventDefault) e.preventDefault();
    actions.openDrawer();
  };

  return (
    <>
      <div ref={headerRef} className={`${cls} app-header`} role="banner">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Link to="/" className="header_logoLink" aria-label="Go to home">
                <img src={logoSrc} alt="logo" className="header_dark_logo" />
              </Link>
              <div className="header_toggle">
                <button
                  type="button"
                  className={`header_toggle_btn header_pickup d-flex align-items-center ${orderMode === 'pickup' ? 'active' : ''}`}
                  onClick={() => openOrderCardForMode('pickup')}
                >
                  <span
                    className="header_toggle_btn__icon"
                    style={{
                      WebkitMaskImage: `url(${bag_icon})`,
                      maskImage: `url(${bag_icon})`,
                    }}
                    aria-hidden="true"
                  />
                  Pick up
                </button>

                <button
                  type="button"
                  className={`header_toggle_btn header_delivery d-flex align-items-center ${orderMode === 'delivery' ? 'active' : ''}`}
                  onClick={() => openOrderCardForMode('delivery')}
                >
                  <span
                    className="header_toggle_btn__icon"
                    style={{
                      WebkitMaskImage: `url(${delivery_icon})`,
                      maskImage: `url(${delivery_icon})`,
                    }}
                    aria-hidden="true"
                  />
                  Delivery
                </button>
              </div>
              <div className="header-address-wrap">
                <button
                  type="button"
                  className="header-address"
                  onClick={toggleOrderCard}
                  title={String(currentAddress?.address || '').trim() || addressFallbackLabel || 'Delivery address'}
                >
                  <img src={locationIcon} alt="" className="header-address__pin" aria-hidden="true" />
                  <span className="header-address__text">
                    {addressDisplay ? (
                      <>
                        <span className="header-address__line">{addressDisplay.line}</span>
                        {addressDisplay.tag ? (
                          <span className="header-address__tag"> · {addressDisplay.tag}</span>
                        ) : null}
                      </>
                    ) : (
                      <span className="header-address__line">{addressFallbackLabel || 'Select delivery address'}</span>
                    )}
                  </span>
                  <span className="header-address__chevron" aria-hidden="true">&#9662;</span>
                </button>
                {orderCardOpen && (
                  <OrderDetailsCard
                    mode={orderMode}
                    onModeChange={setOrderMode}
                    onClose={closeOrderCard}
                  />
                )}
              </div>
            </div>
            <div className="d-flex align-items-center header-right">
              <div className="header-cart-block">
                <img src={cartSrc} alt="" className="header_cart_logo" aria-hidden="true" />
                <button
                  type="button"
                  className="header_cart"
                  onClick={openCart}
                  aria-label={hasItems ? `Cart, ${itemCount} items` : 'Cart'}
                >
                  <span className="header_cart_label">Cart</span>
                  {hasItems ? (
                    <span className="header_cart_badge" aria-hidden="true">
                      {itemCount > 99 ? '99+' : itemCount}
                    </span>
                  ) : null}
                </button>
              </div>
              {isAuthenticated ? (
                <UserMenu
                  userName={displayName}
                  userIconSrc={userSrc}
                  arrowIconSrc={arrow_down}
                  onAccountSettings={() => navigate('/profile/account')}
                  onLogout={logout}
                />
              ) : (
                <GuestAuthDropdown userIconSrc={userSrc} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
